/**
 * Canonical cross-chain swap state, derived from the swap service's already
 * normalized status vocabulary.
 *
 * The swap service folds each provider's raw wire status into a closed
 * vocabulary (see wallet-swap-service internal/services/status.go): the
 * non-terminal values submitted, pending, processing, unknown and the terminal
 * values completed, failed, refunded, partial, expired, timeout. This module is
 * the single client-side place that turns that backend status into the handful
 * of user-facing states the tracker UI renders, and the single place that
 * decides whether a swap is settled. Keeping both here means the poller, the
 * stepper, and Activity all agree without re-parsing provider strings anywhere.
 *
 * The canonical states map to a 3-step stepper (origin confirmed, bridging,
 * delivered) plus the non-error terminal outcomes (refunded, settling) and the
 * one genuine error outcome (failed), and a transient "delayed" flag that is
 * derived from elapsed time, not from the status, so a slow fill reads as
 * taking-longer rather than stuck. Refunded is deliberately kept distinct from
 * failed because the input came back to the user, which is not an error the user
 * needs to act on. Settling is the neutral bucket for a swap that outran the
 * client poll window or reported a provider lapse (expired, timeout): it may
 * still settle server-side, and an unfilled swap is refunded, so it is styled as
 * still-in-progress rather than as a crash.
 */

/**
 * The user-facing lifecycle state of a swap, as the stepper renders it.
 *
 * The three progressive states line up with the stepper rows: `submitting`
 * before the origin transaction is broadcast, `confirming-origin` while the
 * origin deposit confirms, `bridging` once the provider has accepted the
 * deposit and is filling the destination, and `delivered` when the destination
 * fill lands. `refunded`, `settling`, and `failed` are the terminal off-ramps:
 * `refunded` returned the input, `settling` is the neutral still-may-complete
 * bucket (client poll window elapsed, or a provider expired/timeout lapse), and
 * `failed` is the one genuine error.
 */
export type SwapUiState =
  | 'submitting'
  | 'confirming-origin'
  | 'bridging'
  | 'delivered'
  | 'refunded'
  | 'settling'
  | 'failed';

/**
 * The backend's normalized status vocabulary, mirrored here so the mapping is
 * exhaustive against a known set rather than an open string. An unrecognized
 * value (a backend that adds a status this build predates) is treated as
 * non-terminal and still-bridging so the poller keeps going rather than
 * declaring a swap done on a status it does not understand.
 */
export type BackendSwapStatus =
  | 'submitted'
  | 'pending'
  | 'processing'
  | 'unknown'
  | 'completed'
  | 'failed'
  | 'refunded'
  | 'partial'
  | 'expired'
  | 'timeout';

/**
 * A client-only terminal status the poll engine writes when its own tracking
 * window elapses. This is not a backend value: the swap may still settle
 * server-side, so it is kept apart from the provider's own `timeout`/`expired`
 * and mapped to the neutral `settling` UI state (never `failed`), so reaching
 * the client cap surfaces as taking-longer rather than as a failure.
 */
export const CLIENT_TRACK_CAP_STATUS = 'client-timeout';

/**
 * The backend statuses past which no further polling can change the outcome.
 * This matches terminalStatuses in the Go service exactly: a row in one of these
 * has settled and must never be polled back to a non-terminal state. The client
 * poller stops on any of these, unlike the earlier narrow set that omitted
 * partial, expired, and timeout and so would have polled a lapsed swap forever.
 */
const BACKEND_TERMINAL: ReadonlySet<string> = new Set([
  'completed',
  'failed',
  'refunded',
  'partial',
  'expired',
  'timeout',
]);

/** The canonical UI states that are terminal, so the tracker stops on them. */
const UI_TERMINAL: ReadonlySet<SwapUiState> = new Set<SwapUiState>([
  'delivered',
  'refunded',
  'settling',
  'failed',
]);

/**
 * Whether a status should stop the poll loop. This covers the backend's own
 * settled vocabulary plus the client-only cap sentinel: once the client's
 * tracking window has elapsed the loop stops even though the backend row is not
 * itself terminal, because the foreground poll is bounded.
 *
 * @param status The normalized backend status, or the client-cap sentinel.
 * @returns True when the swap has settled (or the client stopped polling) and
 *   further polling is pointless.
 */
export function isBackendTerminal(status: string): boolean {
  const s = status.toLowerCase();
  return s === CLIENT_TRACK_CAP_STATUS || BACKEND_TERMINAL.has(s);
}

/**
 * Whether a canonical UI state is terminal. The tracker stops its poll loop and
 * fires the completion or refund toast on the transition into one of these.
 *
 * @param state The canonical UI state.
 * @returns True when the state is delivered, refunded, or failed.
 */
export function isTerminalUiState(state: SwapUiState): boolean {
  return UI_TERMINAL.has(state);
}

/**
 * Map a backend status to the canonical UI state.
 *
 * The delivered-or-settled cases are unambiguous: completed becomes delivered,
 * refunded stays refunded (kept apart from failed on purpose), and failed
 * becomes failed. expired and timeout, plus the client-cap sentinel, fold into
 * settling rather than failed: they are closer to refund-pending than to a
 * crash (an unfilled swap is refunded to the user's wallet, and a client-cap
 * swap may still settle server-side), so they read as neutral and still-in-
 * progress; the distinct reason stays on the record's status for a detail view.
 * partial is treated as delivered because some output did arrive at the
 * destination, which is the deliver-to-user signal the tracker cares about; the
 * remainder-refund detail is surfaced separately rather than by downgrading the
 * whole swap.
 *
 * The non-terminal cases split on how far the origin leg has progressed. Once an
 * origin transaction hash exists the origin deposit has been broadcast, so
 * pending and processing read as bridging; without a hash yet, submitted and
 * pending read as confirming the origin. This is why the mapper takes the origin
 * hash as well as the status: the same backend status means different things to
 * the user depending on whether the deposit is on-chain.
 *
 * @param status The normalized backend status.
 * @param hasOriginTx Whether an origin transaction hash has been observed.
 * @returns The canonical UI state.
 */
export function toUiState(status: string, hasOriginTx: boolean): SwapUiState {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'partial':
      return 'delivered';
    case 'refunded':
      return 'refunded';
    case 'failed':
      return 'failed';
    case 'expired':
    case 'timeout':
    case CLIENT_TRACK_CAP_STATUS:
      // A provider lapse or the client's own tracking window elapsing is not a
      // crash: the swap may still settle, and an unfilled one is refunded, so it
      // reads as neutral and still-settling rather than a red failure.
      return 'settling';
    case 'processing':
      // The provider is actively filling the destination, so the origin deposit
      // is done regardless of whether we captured its hash yet.
      return 'bridging';
    case 'submitted':
      // Recorded but not yet observed making progress. If the deposit hash is
      // already known the origin is at least broadcast, so show bridging; else
      // it is still confirming on the origin.
      return hasOriginTx ? 'bridging' : 'confirming-origin';
    case 'pending':
      // The provider has accepted the intent and is awaiting or performing the
      // fill; with an origin hash this is the bridging leg, otherwise the origin
      // is still confirming.
      return hasOriginTx ? 'bridging' : 'confirming-origin';
    default:
      // unknown, or a status this build predates: keep polling. If the origin is
      // on-chain call it bridging, else confirming, but never terminal.
      return hasOriginTx ? 'bridging' : 'confirming-origin';
  }
}

/**
 * Which of the three stepper rows a UI state has reached. Row 0 is "Origin
 * confirmed", row 1 "Bridging", row 2 "Delivered". A terminal off-ramp
 * (refunded, settling, failed) reports the bridging row as the last reached,
 * since the swap left the happy path there; the stepper renders those with their
 * own styling rather than lighting the delivered row.
 *
 * @param state The canonical UI state.
 * @returns The zero-based index of the furthest reached step.
 */
export function stepIndexFor(state: SwapUiState): 0 | 1 | 2 {
  switch (state) {
    case 'submitting':
    case 'confirming-origin':
      return 0;
    case 'bridging':
    case 'refunded':
    case 'settling':
    case 'failed':
      return 1;
    case 'delivered':
      return 2;
  }
}

/**
 * Block explorer transaction-page base URLs per standard chain id. Covers the
 * mainnets the swap flow can route and the EVM testnets a swap can execute from,
 * so both the source (origin) and destination hashes link out. The testnet
 * entries are the ones the execute path allows plus the sibling sepolia
 * explorers for the other supported rollups, so a destination on any of them
 * still links. An unmapped chain renders the hash as plain text rather than a
 * dead link.
 */
export const EXPLORER_TX_BASE: Record<number, string> = {
  1: 'https://etherscan.io/tx/',
  10: 'https://optimistic.etherscan.io/tx/',
  56: 'https://bscscan.com/tx/',
  137: 'https://polygonscan.com/tx/',
  8453: 'https://basescan.org/tx/',
  42161: 'https://arbiscan.io/tx/',
  11155111: 'https://sepolia.etherscan.io/tx/',
  84532: 'https://sepolia.basescan.org/tx/',
  421614: 'https://sepolia.arbiscan.io/tx/',
  11155420: 'https://sepolia-optimism.etherscan.io/tx/',
  80002: 'https://amoy.polygonscan.com/tx/',
};

/**
 * Build the explorer URL for a transaction hash on a chain, or null when the
 * chain has no known explorer or the hash is empty. Returning null lets the
 * caller fall back to plain text rather than a broken link.
 *
 * @param chainId The standard chain id the transaction is on.
 * @param hash The transaction hash, 0x-prefixed.
 * @returns The absolute explorer URL, or null when it cannot be built.
 */
export function explorerTxUrl(chainId: number | undefined, hash: string | undefined | null): string | null {
  if (!hash || chainId === undefined) return null;
  const base = EXPLORER_TX_BASE[chainId];
  if (!base) return null;
  return `${base}${hash}`;
}

/**
 * Block explorer transaction-page base URLs per wallet chain key. The swap flow
 * keys its explorer links by numeric EVM chain id (EXPLORER_TX_BASE above), but a
 * wallet history row carries the gateway's string chain key rather than a chain
 * id, and spans non-EVM chains the swap flow never touches. This registry covers
 * every chain a wallet can hold: the EVM networks (mainnet plus each testnet the
 * app supports) keyed by their gateway chain key, plus Bitcoin, Solana, and Sui
 * on both their mainnet and testnet keys. Each base ends at the explorer's
 * transaction path so a hash appends directly. Bitcoin and Solana take a query
 * suffix (a testnet cluster or network) rather than a distinct host, so the base
 * carries that suffix and the hash is inserted before it, handled by
 * explorerTxUrlForChain below rather than by plain concatenation.
 */
const CHAIN_EXPLORER_TX: Record<string, string> = {
  // EVM mainnets. The unified-EVM sentinel and the bare ethereum key both point
  // at Etherscan, since a unified-EVM wallet's native chain is Ethereum.
  evm: 'https://etherscan.io/tx/',
  ethereum: 'https://etherscan.io/tx/',
  base: 'https://basescan.org/tx/',
  arbitrum: 'https://arbiscan.io/tx/',
  optimism: 'https://optimistic.etherscan.io/tx/',
  polygon: 'https://polygonscan.com/tx/',
  bsc: 'https://bscscan.com/tx/',
  // EVM testnets, matching the per-network chain keys the send and balance paths
  // already use.
  sepolia: 'https://sepolia.etherscan.io/tx/',
  'base-sepolia': 'https://sepolia.basescan.org/tx/',
  'arbitrum-sepolia': 'https://sepolia.arbiscan.io/tx/',
  'optimism-sepolia': 'https://sepolia-optimism.etherscan.io/tx/',
  'polygon-amoy': 'https://amoy.polygonscan.com/tx/',
  'bsc-testnet': 'https://testnet.bscscan.com/tx/',
  // Bitcoin. mempool.space serves both networks under one host with a testnet
  // path segment, so the mainnet and testnet keys differ only in that segment.
  bitcoin: 'https://mempool.space/tx/',
  'bitcoin-mainnet': 'https://mempool.space/tx/',
  'bitcoin-taproot': 'https://mempool.space/tx/',
  'bitcoin-taproot-mainnet': 'https://mempool.space/tx/',
  'bitcoin-testnet': 'https://mempool.space/testnet/tx/',
  // Solana. Solscan reads the cluster from a `?cluster=` query, so the Testnet
  // and Devnet keys carry that suffix and the hash is spliced in before it.
  solana: 'https://solscan.io/tx/',
  'solana-testnet': 'https://solscan.io/tx/?cluster=testnet',
  'solana-devnet': 'https://solscan.io/tx/?cluster=devnet',
  // Sui. SuiScan serves mainnet and testnet under distinct hosts.
  sui: 'https://suiscan.xyz/mainnet/tx/',
  'sui-testnet': 'https://suiscan.xyz/testnet/tx/',
  'sui-devnet': 'https://suiscan.xyz/devnet/tx/',
};

/**
 * Build the explorer URL for a transaction hash given the gateway's string chain
 * key, or null when the chain is unmapped or the hash is empty. This is the
 * wallet-history counterpart to {@link explorerTxUrl}, which keys off a numeric
 * EVM chain id; a history row carries the chain key instead and can be on a
 * non-EVM chain. A base that already carries a query string (a Solana cluster)
 * has the hash inserted before the `?` so the cluster stays a query parameter
 * rather than part of the path. Returning null lets the row fall back to plain
 * text rather than a dead link.
 *
 * @param chain The gateway chain key, for example "ethereum", "bitcoin", or
 *   "solana-testnet". Case-insensitive.
 * @param hash The transaction hash. Empty or null yields null.
 * @returns The absolute explorer URL, or null when it cannot be built.
 */
export function explorerTxUrlForChain(
  chain: string | undefined | null,
  hash: string | undefined | null,
): string | null {
  if (!hash || !chain) return null;
  const base = CHAIN_EXPLORER_TX[chain.toLowerCase()];
  if (!base) return null;
  const q = base.indexOf('?');
  // Splice the hash before any query string so a cluster suffix stays a query
  // parameter; otherwise the hash simply appends to the transaction path.
  return q === -1 ? `${base}${hash}` : `${base.slice(0, q)}${hash}${base.slice(q)}`;
}

/**
 * Abbreviate a transaction hash for display, keeping the leading and trailing
 * characters so it stays recognizable, for example "0x2775…e14a".
 *
 * @param hash The full hash.
 * @returns The shortened form, or the input unchanged when already short.
 */
export function shortHash(hash: string): string {
  return hash.length > 14 ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : hash;
}
